import { useState, useEffect, useCallback, useRef } from 'react';
import config from '../config';
import './table.css'
import TableData from './tabledata';
import DashboardBarChart from './dashboardBarChart';
import * as XLSX from 'xlsx';


function Table({ onTabChange, selectedCards, filters }) {
    const tabs = ['Vertical', 'Division', 'Artist', 'Work Type', 'Task Type'];
    const [activeTab, setActiveTab] = useState('Vertical');
    const [tableData, setTableData] = useState([]);
    const [graphData, setGraphData] = useState(null);
    const [headerTag, setHeaderTag] = useState("Vertical");
    const [graphView, setGraphView] = useState(false);
    const [filterList, setFilterList] = useState({ artists: [], divisions: [], taskTypes: [] });
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Define the missing toggleGraphView function
    const toggleGraphView = () => {
        // Implement your logic here, e.g., toggle a state or show a graph
        setGraphView((prev) => !prev);
    };

    const transformData = (apiResponse, type) => {
        const transformed = [];

        switch (type) {
            case "dtable":
                setHeaderTag("Vertical"); // Default tag
                let overallTotalRow = null;
                for (const [key, value] of Object.entries(apiResponse.result)) {
                    const row = {
                        Vertical: key.charAt(0).toUpperCase() + key.slice(1),
                        Completed: parseInt(value.project_status.completed) || null,
                        Ongoing: parseInt(value.project_status.ongoing) || null,
                        Upcoming: parseInt(value.project_status.upcoming) || null,
                        "Total Work Count": parseInt(value.work_count) || null,
                    };

                    if (key === "overall_total") {
                        overallTotalRow = row;
                    } else {
                        transformed.push(row);
                    }
                }

                if (overallTotalRow) {
                    transformed.push(overallTotalRow); // Add it at the end
                }

                break;

            case "division":
                setHeaderTag("Division"); // Update header tag
                apiResponse.result.forEach((item) => {
                    transformed.push({
                        Vertical: item.division,
                        Completed: parseInt(item.project_status.completed) || null,
                        Ongoing: parseInt(item.project_status.ongoing) || null,
                        Upcoming: parseInt(item.project_status.upcoming) || null,
                        "Total Work Count": parseInt(item.total_count) || null,
                    });
                });
                break;

            case "artist":
                setHeaderTag("Artist"); // Update header tag
                apiResponse.result.forEach((item) => {
                    transformed.push({
                        Vertical: item.artist_name,
                        Completed: parseInt(item.project_status.completed) || null,
                        Ongoing: parseInt(item.project_status.ongoing) || null,
                        Upcoming: parseInt(item.project_status.upcoming) || null,
                        "Total Work Count": parseInt(item.total_count) || null,
                    });
                });
                break;
            case "worktype":
                setHeaderTag("Vertical"); // Update header tag
                apiResponse.result.forEach((category) => {
                    const { work_category, work_type, work_count } = category;

                    // Flatten the work_type array and include rowspan for the first row
                    work_type.forEach((type, index) => {
                        transformed.push({
                            Vertical: index === 0 ? work_category : null, // Set Vertical only for the first row
                            WorkType: type.qText, // New, Mod, Overdue
                            Completed: parseInt(type.project_status.completed) || null,
                            Ongoing: parseInt(type.project_status.ongoing) || null,
                            Upcoming: parseInt(type.project_status.upcoming) || null,
                            Total: parseInt(type.project_status.total_count) || null,
                            "Grand Total": index === 0 ? parseInt(work_count) : null,
                            // RowSpan: index === 0 ? work_type.length : null, // Set RowSpan only for the first row
                        });
                    });
                });
                // setTableData(apiResponse);
                break;

            case "tasktype":
                setHeaderTag("Task Type"); // Update header tag
                apiResponse.result.forEach((item) => {
                    transformed.push({
                        Vertical: item.task_type,
                        New: parseInt(item.work_type.new) || null,
                        Mod: parseInt(item.work_type.mod) || null,
                        Overdue: parseInt(item.work_type.overdue) || null,
                        "Total Work Count": parseInt(item.total_work_count) || null,
                    });
                });
                break;

            default:
                break;
        }

        return transformed;
    };


    const buildQueryParams = useCallback(() => {
        let queryParts = [];

        queryParts.push(`viewall=false`);

        if (filters.selectedVerticals?.length > 0) {
            const categories = filters.selectedVerticals
                .map(v => v.toLowerCase().replace(/\s+/g, "_"))
                .join(",");
            queryParts.push(`category=${categories}`);
        }

        if (filters.selectedStatuses?.length > 0) {
            const statuses = filters.selectedStatuses
                .map(s => s.replace(/\s+/g, "_"))
                .join(",");
            queryParts.push(`project_status=${statuses}`);
        }

        if (filters.selectedDate && filters.selectedDate !== "Custom") {
            const range = getDateRange(filters.selectedDate);
            if (range) {
                queryParts.push(`start_date=${range.start_date}`);
                queryParts.push(`end_date=${range.end_date}`);
            }
        } else if (filters.selectedDate === "Custom" && filters.fromYear && filters.toYear) {
            const startDate = `${filters.fromYear}-${String(filters.fromMonth).padStart(2, "0")}-${String(filters.fromDay || "01").padStart(2, "0")}`;
            const endDate = `${filters.toYear}-${String(filters.toMonth).padStart(2, "0")}-${String(filters.toDay || "01").padStart(2, "0")}`;
            queryParts.push(`start_date=${startDate}`);
            queryParts.push(`end_date=${endDate}`);
        }

        if (selectedItems?.length > 0) {
            // Group items by type
            const grouped = selectedItems.reduce((acc, item) => {
                if (!acc[item.type]) acc[item.type] = [];
                acc[item.type].push(item.value); // use .value for API calls
                return acc;
            }, {});

            // Artists → use IDs
            if (grouped["Artist"]?.length > 0) {
                queryParts.push(`artist=${grouped["Artist"].join(",")}`);
            }

            // Divisions → lowercase + underscore
            if (grouped["Division"]?.length > 0) {
                // const divisions = grouped["Division"]
                //     .map(d => d.toLowerCase().replace(/\s+/g, "_"))
                //     .join(",");
                queryParts.push(`division=${grouped["Division"].join(",")}`);
            }

            // Task Types → lowercase + underscore
            if (grouped["Task Type"]?.length > 0) {
                // const taskTypes = grouped["Task Type"]
                //     .map(t => t.toLowerCase().replace(/\s+/g, "_"))
                //     .join(",");
                queryParts.push(`taskType=${grouped["Task Type"].join(",")}`);
            }
        }


        return queryParts.join("&"); // final query string
    }, [filters, selectedItems]);

    const getDateRange = (selectedDate) => {
        const today = new Date();
        let startDate, endDate;

        switch (selectedDate) {
            case "Today":
                startDate = endDate = today;
                break;

            case "Last Week":
                endDate = today;
                startDate = new Date();
                startDate.setDate(today.getDate() - 7);
                break;

            case "Last Month":
                endDate = today;
                startDate = new Date();
                startDate.setMonth(today.getMonth() - 1);
                break;

            case "Last Year":
                endDate = today;
                startDate = new Date();
                startDate.setFullYear(today.getFullYear() - 1);
                break;

            default:
                return null; // for Custom case
        }

        // format YYYY-MM-DD
        const formatDate = (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                d.getDate()
            ).padStart(2, "0")}`;

        return {
            start_date: formatDate(startDate),
            end_date: formatDate(endDate),
        };
    };




    useEffect(() => {
        const fetchData = async () => {
            try {
                let url = "";
                let type = "";

                switch (activeTab) {
                    case "Vertical":
                        url = "https://alembicdigilabs.in/digilabs/mis/dtable";
                        type = "dtable";
                        break;
                    case "Division":
                        url = "https://alembicdigilabs.in/digilabs/mis/division";
                        type = "division";
                        break;
                    case "Artist":
                        url = "https://alembicdigilabs.in/digilabs/mis/artist";
                        type = "artist";
                        break;
                    case "Work Type":
                        url = "https://alembicdigilabs.in/digilabs/mis/worktype";
                        type = "worktype";
                        break;
                    case "Task Type":
                        url = "https://alembicdigilabs.in/digilabs/mis/tasktype";
                        type = "tasktype";
                        break;
                    default:
                        console.error("Invalid activeTab value");
                        return;
                }

                let finalUrl = buildQueryParams();
                console.log("Fetching data from:", finalUrl);

                const response = await fetch(`${url}?${finalUrl}`);
                const data = await response.json();
                const formattedData = transformData(data, type);
                setTableData(formattedData);
                const graphData = data.categoryChart
                setGraphData(graphData);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [activeTab, buildQueryParams]);

    useEffect(() => {
        const fetchFilters = async () => {
            const response = await fetch("https://alembicdigilabs.in/digilabs/mis/filterList");
            const data = await response.json();
            setFilterList(data);
        };
        fetchFilters();
    }, []);

    useEffect(() => {
        const keyword = (search || "").toLowerCase();

        // Artists → keep FULL objects (id + name)
        let artistMatches = [];
        if (keyword) {
            artistMatches = (filterList.artists ?? []).filter(a =>
                a.artist_name?.toLowerCase().includes(keyword)
            );
        } else {
            artistMatches = (filterList.artists ?? []).slice(0, 2);
        }

        // Divisions → strings
        let divisionMatches = [];
        if (keyword) {
            divisionMatches = (filterList.divisions ?? []).filter(d =>
                d?.toLowerCase().includes(keyword)
            );
        } else {
            divisionMatches = (filterList.divisions ?? []).slice(0, 2);
        }

        // Task Types → strings
        let taskMatches = [];
        if (keyword) {
            taskMatches = (filterList.taskTypes ?? []).filter(t =>
                t?.toLowerCase().includes(keyword)
            );
        } else {
            taskMatches = (filterList.taskTypes ?? []).slice(0, 2);
        }

        const grouped = {};
        if (artistMatches.length) grouped["Artist"] = artistMatches;          // array of {artist_id, artist_name}
        if (divisionMatches.length) grouped["Division"] = divisionMatches;    // array of strings
        if (taskMatches.length) grouped["Task Type"] = taskMatches;           // array of strings
        
        setResults(grouped);
    }, [search, filterList, selectedItems]);


    const handleSelect = (type, item) => {
        const label = item.artist_name || item;
        const value = item.artist_id ?? item;

        const exists = selectedItems.some(i => i.type === type && i.value === value);
        if (!exists) {
            setSelectedItems(prev => [...prev, { type, label, value }]);
        }
        console.log(label, value);

        setSearch("");
        setResults({});
    };


    const handleRemove = (label) => {
        setSelectedItems(selectedItems.filter((item) => item.label !== label));
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dropdownRef]);

    const convertJsonToExcel = () => {
        if (!tableData || tableData.length === 0) return;

        // Dynamically extract headers from the first object
        const headers = Object.keys(tableData[0]);

        // Build rows (first row = headers)
        const rows = [headers];

        // Fill rows with values, replace null with 0
        tableData.forEach((item) => {
            const row = headers.map((key) => item[key] ?? 0);
            rows.push(row);
        });

        // Create worksheet and workbook
        const worksheet = XLSX.utils.aoa_to_sheet(rows);

        // Auto-adjust column widths
        const colWidths = headers.map((header, i) => {
            let maxLength = header.length;
            rows.forEach((row) => {
                const cellValue = row[i] ? row[i].toString() : "";
                maxLength = Math.max(maxLength, cellValue.length);
            });
            return { wch: maxLength + 2 }; // +2 for padding
        });
        worksheet["!cols"] = colWidths;

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

        // Export Excel file
        XLSX.writeFile(workbook, "data.xlsx");
    };


    return (
        <div>
            <div className='action_wrapper'>
                <div className='tabs'>
                    {tabs.map((item, index) => {
                        return <span
                            className={`${item === activeTab ? 'activeTab' : 'inactive'}`} key={index}
                            onClick={() => { setActiveTab(item); onTabChange(item); }}>
                            {item}</span>
                    })}
                </div>
                <div className='downloadFilter_section'>
                    <div className='header-action-1' style={{ gap: '8px', width: '150px' }}>
                        <input type="checkbox" id='filter' onClick={toggleGraphView} />
                        <label htmlFor='filter' style={{ textWrap: 'nowrap' }}>Graph View</label>
                    </div>
                    <img src={config.download} alt='' style={{ width: '2rem' }} onClick={convertJsonToExcel} />
                    <div style={{ position: 'relative', width: '20rem' }} ref={dropdownRef}>

                        <input
                            type="text"
                            placeholder="Search artist, division, task type..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onFocus={() => setShowDropdown(true)}
                            className='search-input'
                        />

                        {selectedItems.length > 0 && <div style={{ position: "absolute", top: "21%", right: "5%", background: "rgba(73, 26, 203, 1)", color: "#fff", borderRadius: "50%", width: "20px", height: "20px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px" }}>
                            {selectedItems.length}
                        </div> }

                        {showDropdown && (
                            <div className='dropdown-wrapper'>
                                {selectedItems.length > 0 && (
                                    <div className='selected-items'>
                                        <strong>
                                            Selected :
                                        </strong>
                                        {selectedItems.map((item, idx) => (
                                            <span
                                                key={idx} className='selected-item'
                                            >
                                                {item.label}
                                                <span
                                                    style={{ cursor: "pointer", fontWeight: "bold" }}
                                                    onClick={() => handleRemove(item.label)}
                                                >
                                                    ×
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {Object.entries(results).map(([type, items], typeIdx, arr) => (
                                    <div key={type} style={{ marginBottom: "10px", marginTop: typeIdx === 0 ? "" : "20px", paddingTop: "10px", borderTop: typeIdx === 0 ? "none" : "1px solid #ccc", }}>
                                        <div style={{
                                            display: "block", marginBottom: "5px", color: "#585858",
                                           
                                        }}>
                                            {type}
                                        </div>
                                        {items.map((item, idx) => (
                                            <div
                                                key={item.artist_id || idx}
                                                style={{
                                                    padding: "5px",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleSelect(type, item)}
                                            >
                                                {item.artist_name || item}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {!graphView ?
                <TableData data={tableData} headerTag={headerTag} activeTab={activeTab} /> :
                <DashboardBarChart lineChartData={graphData} />
            }
        </div>
    )
}

export default Table;
